import React, { useMemo } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { GLASS } from "./InspiredBackground.js";
import { useLanguage } from "../i18n/LanguageContext.js";
import { quoteAuthor, quoteText } from "../content/dailyQuotes.js";

/** @param {string | undefined} startDate YYYY-MM-DD */
function formatElapsedSince(startDate, hrLabel = "Hr", minLabel = "Mins") {
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
    return `0 ${hrLabel} 0 ${minLabel}`;
  }
  const start = new Date(`${startDate}T00:00:00`);
  const now = new Date();
  const ms = Math.max(0, now.getTime() - start.getTime());
  const hrs = Math.floor(ms / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  return `${hrs} ${hrLabel} ${mins} ${minLabel}`;
}

/**
 * Thick progress ring with a gap at 12 o'clock (mockup hero).
 * @param {{ progress: number, size: number, stroke: number, children?: React.ReactNode }} props
 */
function HeroProgressRing({ progress, size, stroke, children }) {
  const p = Math.min(0.94, Math.max(0.1, progress));
  const active = (n) => (p >= n ? CF.primaryBright : "transparent");

  return (
    <View style={{ width: size, height: size, alignItems: "center", justifyContent: "center" }}>
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderColor: CF.ringTrack,
        }}
      />
      <View
        style={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: size / 2,
          borderWidth: stroke,
          borderTopColor: active(0.001),
          borderRightColor: active(0.25),
          borderBottomColor: active(0.5),
          borderLeftColor: active(0.75),
          transform: [{ rotate: "-90deg" }],
        }}
      />
      <View style={{ alignItems: "center", justifyContent: "center", paddingHorizontal: 8 }}>{children}</View>
    </View>
  );
}

/**
 * @param {{
 *   habitName: string,
 *   currentStreak: number,
 *   goalProgressPct: number,
 *   startDate?: string,
 *   quoteOfTheDay: { text: string, author?: string } | null,
 *   notificationBadgeCount: number,
 *   compact?: boolean,
 *   showProtectionBar?: React.ReactNode,
 *   onOpenMenu: () => void,
 *   onOpenNotifications: () => void,
 *   onMarkSuccess: () => void,
 *   onMarkFail: () => void,
 *   onUrge: () => void,
 * }} props
 */
export function HomeDashboard({
  habitName,
  currentStreak,
  goalProgressPct,
  startDate,
  quoteOfTheDay,
  notificationBadgeCount,
  compact = false,
  showProtectionBar,
  onOpenMenu,
  onOpenNotifications,
  onMarkSuccess,
  onMarkFail,
  onUrge,
}) {
  const { t, locale } = useLanguage();
  const ringSize = compact ? 200 : 228;
  const ringStroke = compact ? 10 : 12;

  const ringProgress = useMemo(() => {
    const base = goalProgressPct / 100;
    if (currentStreak <= 0) return 0.12;
    return Math.min(0.94, Math.max(0.35, base * 0.55 + 0.42));
  }, [goalProgressPct, currentStreak]);

  const displayQuote = quoteOfTheDay ? quoteText(quoteOfTheDay, locale) : "";
  const displayAuthor = quoteAuthor(quoteOfTheDay);

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[styles.content, compact && styles.contentCompact]}
      showsVerticalScrollIndicator={false}
    >
      {showProtectionBar ? <View style={styles.protectionSlot}>{showProtectionBar}</View> : null}

      <View style={styles.header}>
        <Pressable style={styles.logoBtn} onPress={onOpenMenu} hitSlop={8}>
          <View style={styles.logoCircle}>
            <Text style={styles.logoMark}>CF</Text>
          </View>
        </Pressable>
        <View style={styles.brandWrap}>
          <Text style={styles.brandTitle} numberOfLines={1}>
            {habitName || "Procsystim"}
          </Text>
        </View>
        <Pressable style={styles.bellBtn} onPress={onOpenNotifications} hitSlop={8}>
          <Text style={styles.bellIcon}>🔔</Text>
          {notificationBadgeCount > 0 ? (
            <View style={styles.bellBadge}>
              <Text style={styles.bellBadgeText}>{notificationBadgeCount > 9 ? "9+" : notificationBadgeCount}</Text>
            </View>
          ) : null}
        </Pressable>
      </View>

      <View style={styles.hero}>
        <HeroProgressRing progress={ringProgress} size={ringSize} stroke={ringStroke}>
          <Text style={[styles.streakNumber, compact && styles.streakNumberCompact]}>{currentStreak}</Text>
          <Text style={styles.streakUnit}>{t("home_days")}</Text>
          <Text style={styles.streakElapsed}>
            {formatElapsedSince(startDate, locale === "fr" ? "h" : "Hr", locale === "fr" ? "min" : "Mins")}
          </Text>
        </HeroProgressRing>
      </View>

      <View style={styles.quoteCard}>
        <Text style={styles.quoteLabel}>{t("home_quote_label")}</Text>
        {displayQuote ? (
          <>
            <Text style={styles.quoteText}>"{displayQuote}"</Text>
            {displayAuthor ? <Text style={styles.quoteAuthor}>— {displayAuthor}</Text> : null}
          </>
        ) : (
          <Text style={styles.quoteMuted}>{t("home_quote_loading")}</Text>
        )}
      </View>

      <View style={styles.actions}>
        <View style={styles.successFailRow}>
          <Pressable style={[styles.actionBtn, styles.successBtn]} onPress={onMarkSuccess}>
            <Text style={styles.actionBtnText}>{t("home_success")}</Text>
          </Pressable>
          <Pressable style={[styles.actionBtn, styles.failBtn]} onPress={onMarkFail}>
            <Text style={styles.actionBtnText}>{t("home_fail")}</Text>
          </Pressable>
        </View>

        <Pressable style={styles.urgeCircle} onPress={onUrge} accessibilityLabel={t("home_urge")}>
          <Text style={styles.urgeCircleText}>{t("home_urge")}</Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: "transparent" },
  content: {
    flexGrow: 1,
    paddingHorizontal: 18,
    paddingBottom: 12,
    paddingTop: 8,
  },
  contentCompact: { paddingTop: 4 },
  protectionSlot: { marginBottom: 6 },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
    minHeight: 44,
  },
  logoBtn: { width: 44, alignItems: "flex-start" },
  logoCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: GLASS.cardStrong,
    borderWidth: 1.5,
    borderColor: GLASS.border,
    alignItems: "center",
    justifyContent: "center",
  },
  logoMark: {
    fontSize: 13,
    fontWeight: "900",
    color: GLASS.textMid,
    letterSpacing: -0.5,
  },
  brandWrap: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 10,
  },
  brandTitle: {
    textAlign: "center",
    fontSize: 20,
    fontWeight: "700",
    color: GLASS.textDark,
    letterSpacing: 0.3,
  },
  bellBtn: { width: 44, alignItems: "flex-end", position: "relative" },
  bellIcon: { fontSize: 22, opacity: 0.65 },
  bellBadge: {
    position: "absolute",
    top: -2,
    right: -2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: CF.fail,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 3,
  },
  bellBadgeText: { color: "#fff", fontSize: 9, fontWeight: "800" },
  hero: {
    alignItems: "center",
    marginTop: 4,
    marginBottom: 16,
  },
  streakNumber: {
    fontSize: 56,
    fontWeight: "800",
    color: GLASS.textDark,
    lineHeight: 60,
    letterSpacing: -1,
  },
  streakNumberCompact: { fontSize: 48, lineHeight: 52 },
  streakUnit: {
    fontSize: 15,
    fontWeight: "500",
    color: GLASS.textSoft,
    marginTop: -2,
  },
  streakElapsed: {
    fontSize: 13,
    fontWeight: "700",
    color: GLASS.textMid,
    marginTop: 6,
    letterSpacing: 0.2,
  },
  quoteCard: {
    backgroundColor: GLASS.cardStrong,
    borderRadius: 16,
    padding: 18,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: GLASS.border,
  },
  quoteLabel: {
    fontSize: 11,
    fontWeight: "800",
    color: GLASS.label,
    letterSpacing: 1,
    textTransform: "uppercase",
    marginBottom: 10,
    textAlign: "center",
  },
  quoteText: {
    fontSize: 16,
    lineHeight: 24,
    color: GLASS.textDark,
    textAlign: "center",
    fontStyle: "italic",
  },
  quoteAuthor: {
    marginTop: 12,
    fontSize: 13,
    fontWeight: "600",
    color: GLASS.textSoft,
    textAlign: "center",
  },
  quoteMuted: {
    fontSize: 14,
    color: GLASS.textSoft,
    textAlign: "center",
  },
  actions: {
    alignItems: "center",
    gap: 16,
    marginTop: 4,
  },
  successFailRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  actionBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 999,
    alignItems: "center",
    borderWidth: 1.5,
  },
  successBtn: {
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    borderColor: CF.primaryDark,
  },
  failBtn: {
    backgroundColor: "rgba(255, 255, 255, 0.65)",
    borderColor: CF.fail,
  },
  actionBtnText: {
    color: GLASS.textDark,
    fontSize: 15,
    fontWeight: "800",
  },
  urgeCircle: {
    width: 112,
    height: 112,
    borderRadius: 56,
    backgroundColor: "rgba(255, 255, 255, 0.55)",
    borderWidth: 1.5,
    borderColor: CF.purpleAccent,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  urgeCircleText: {
    color: CF.purpleAccent,
    fontSize: 13,
    fontWeight: "800",
    textAlign: "center",
    lineHeight: 17,
  },
});
